import { Navigate, Route, Routes } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { usePageTracking } from './hooks/usePageTracking';
import { useInteractionTracking } from './hooks/useInteractionTracking';
import { Layout } from './components/Layout';
import { WorkspaceLayout } from './components/WorkspaceLayout';
import { WorkspaceSettingsPage } from './pages/WorkspaceSettingsPage';
import { NoWorkspacePage } from './pages/NoWorkspacePage';
import { OAuthConsentPage } from './pages/OAuthConsentPage';
import { ProtectedRoute } from './components/ProtectedRoute';
import { SlotPathRedirect } from './components/SlotPathRedirect';
import { RequireTwoFactor } from './components/RequireTwoFactor';
import { LoginPage } from './pages/LoginPage';
import { LoginMfaPage } from './pages/LoginMfaPage';
import { AccountsPage } from './pages/AccountsPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { SetupPage } from './pages/SetupPage';
import { EnrollTwoFactorPage } from './pages/EnrollTwoFactorPage';
import { ProjectsPage } from './pages/ProjectsPage';
import { OverviewPage } from './pages/OverviewPage';
import { ActivityPage } from './pages/ActivityPage';
import { PrototypesPage } from './pages/PrototypesPage';
import { PrototypeDetailPage } from './pages/PrototypeDetailPage';
import { PrototypeSettingsPage } from './pages/PrototypeSettingsPage';
import { ProjectPage } from './pages/ProjectPage';
import { IssueEditorPage } from './pages/IssueEditorPage';
import { IssueDetailPage } from './pages/IssueDetailPage';
import { RunDetailPage } from './pages/RunDetailPage';
import {
  SettingsPage,
  GeneralSettingsSection,
  NotificationsSettingsSection,
  McpSettingsSection,
  SecuritySettingsSection,
} from './pages/SettingsPage';
import { InvitationAcceptPage } from './pages/InvitationAcceptPage';
import { MetricsPage } from './pages/MetricsPage';
import { DocsPage } from './pages/DocsPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { LoopsPage } from './pages/LoopsPage';
import { AgentsPage } from './pages/AgentsPage';
import { LoopEditorPage } from './pages/LoopEditorPage';
import { ProjectThreadsPage } from './pages/ProjectThreadsPage';
import { NewThreadPage } from './pages/NewThreadPage';
import { ThreadPage } from './pages/ThreadPage';
import { LegacyChatRedirect } from './components/threads/LegacyChatRedirect';
import { fetchSetupStatus } from './api';

export default function App() {
  usePageTracking();
  useInteractionTracking();
  // Gate the whole app on first-run setup state (public endpoint).
  const { data, isLoading } = useQuery({
    queryKey: ['setup-status'],
    queryFn: fetchSetupStatus,
    // Finite staleTime (not Infinity) + refetch on focus so the app notices a
    // backend setup-state change (e.g. setup completed elsewhere) without a
    // hard reload.
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    retry: false,
  });

  // Zero accounts → force the wizard, nothing else is reachable.
  // Guard is skipped while loading so the login form is in the DOM immediately —
  // blocking with a spinner prevents password managers from detecting the form.
  if (!isLoading && data?.needsSetup) {
    return (
      <Routes>
        <Route path="/setup" element={<SetupPage />} />
        <Route path="*" element={<Navigate to="/setup" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/setup" element={<Navigate to="/" replace />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/accounts" element={<AccountsPage />} />
      {/* Own route (not an in-place swap) so password managers see a
          navigation and re-scan for the OTP field. */}
      <Route path="/login/mfa" element={<LoginMfaPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      {/* Invitation accept — accessible without auth so new users can register */}
      <Route path="/invitations/accept" element={<InvitationAcceptPage />} />
      {/* Mandatory 2FA enrollment — authenticated, but OUTSIDE RequireTwoFactor
          (which redirects here) so there's no loop, and full-screen (no app
          nav). */}
      <Route
        path="/security/enroll"
        element={
          <ProtectedRoute>
            <EnrollTwoFactorPage />
          </ProtectedRoute>
        }
      />
      {/* Full-screen (no workspace nav) landing for users who belong to no
          workspace — a workspace is provisioned by an operator, not auto-created. */}
      <Route
        path="/no-workspace"
        element={
          <ProtectedRoute>
            <RequireTwoFactor>
              <NoWorkspacePage />
            </RequireTwoFactor>
          </ProtectedRoute>
        }
      />
      {/* MCP OAuth consent: full-screen, no app nav. An MCP client sends the
          browser here (via GET /api/oauth/authorize); login returns here with
          the query intact. */}
      <Route
        path="/oauth/consent"
        element={
          <ProtectedRoute>
            <RequireTwoFactor>
              <OAuthConsentPage />
            </RequireTwoFactor>
          </ProtectedRoute>
        }
      />
      <Route
        element={
          <ProtectedRoute>
            <RequireTwoFactor>
              <Layout />
            </RequireTwoFactor>
          </ProtectedRoute>
        }
      >
        {/* The entry layer is GLOBAL: Overview, Projects and Prototypes
            aggregate across every workspace the user belongs to. You enter a
            workspace by opening one of its items (which routes to /w/<slug>/…). */}
        <Route path="/" element={<OverviewPage />} />
        <Route path="/projects" element={<ProjectsPage />} />
        <Route path="/prototypes" element={<PrototypesPage />} />
        {/* Activity: the pre-hub dashboard. */}
        <Route path="/activity" element={<ActivityPage />} />
        {/* Legacy top-level search path folds into the global Overview. */}
        <Route path="/search" element={<Navigate to="/" replace />} />

        {/* Workspace-scoped: item detail + workspace tools live under /w/:slug so
            the URL names the workspace they belong to. WorkspaceLayout makes
            :slug active. The lists/overview above are global, so a bare
            /w/:slug redirects up to the global Overview. */}
        <Route path="/w/:slug" element={<WorkspaceLayout />}>
          <Route index element={<Navigate to="/" replace />} />
          <Route path="projects" element={<Navigate to="/projects" replace />} />
          <Route path="projects/:projectId" element={<ProjectPage />} />
          <Route path="projects/:projectId/threads" element={<ProjectThreadsPage />} />
          <Route path="projects/:projectId/threads/new" element={<NewThreadPage />} />
          <Route path="projects/:projectId/threads/:threadId" element={<ThreadPage />} />
          <Route path="projects/:projectId/chat/:sessionId?" element={<LegacyChatRedirect kind="repo" />} />
          <Route path="projects/:projectId/loops" element={<LoopsPage />} />
          <Route path="projects/:projectId/agents" element={<AgentsPage />} />
          <Route path="projects/:projectId/loops/new" element={<LoopEditorPage mode="create" />} />
          <Route path="projects/:projectId/loops/:loopId/edit" element={<LoopEditorPage mode="edit" />} />
          <Route path="projects/:projectId/issues/new" element={<IssueEditorPage mode="create" />} />
          <Route path="prototypes" element={<Navigate to="/prototypes" replace />} />
          <Route path="prototypes/:id" element={<PrototypeDetailPage />} />
          <Route path="prototypes/:projectId/threads" element={<ProjectThreadsPage />} />
          <Route path="prototypes/:projectId/threads/new" element={<NewThreadPage />} />
          <Route path="prototypes/:projectId/threads/:threadId" element={<ThreadPage />} />
          <Route path="prototypes/:projectId/chat/:sessionId?" element={<LegacyChatRedirect kind="prototype" />} />
          <Route path="prototypes/:projectId/settings" element={<PrototypeSettingsPage />} />
          <Route path="prototypes/:projectId/loops" element={<LoopsPage />} />
          <Route path="prototypes/:projectId/agents" element={<AgentsPage />} />
          <Route path="prototypes/:projectId/loops/new" element={<LoopEditorPage mode="create" />} />
          <Route path="prototypes/:projectId/loops/:loopId/edit" element={<LoopEditorPage mode="edit" />} />
          <Route path="prototypes/:projectId/issues/new" element={<IssueEditorPage mode="create" />} />
          <Route path="issues/:issueId" element={<IssueDetailPage />} />
          <Route path="issues/:issueId/edit" element={<IssueEditorPage mode="edit" />} />
          <Route path="runs/:runId" element={<RunDetailPage />} />
          <Route path="metrics" element={<MetricsPage />} />
          {/* Workspace settings: members + rename. Account settings live at
              the global /settings below. */}
          <Route path="settings" element={<WorkspaceSettingsPage />} />
        </Route>

        {/* Global (not workspace-scoped): account, docs. */}
        {/* /security folded into the Settings page's Security tab. */}
        <Route
          path="/security"
          element={<Navigate to="/settings/security" replace />}
        />
        <Route path="/settings" element={<SettingsPage />}>
          <Route index element={<Navigate to="/settings/general" replace />} />
          <Route path="general" element={<GeneralSettingsSection />} />
          <Route path="notifications" element={<NotificationsSettingsSection />} />
          <Route path="mcp" element={<McpSettingsSection />} />
          <Route path="security" element={<SecuritySettingsSection />} />
        </Route>
        <Route path="/docs" element={<DocsPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
      {/* /u/0/... drops its prefix; /u/<not 0-4>/... is not a valid account slot */}
      <Route path="/u/*" element={<SlotPathRedirect />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
